import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import NavBar from './components/NavBar';
import MoviesList from './components/MovieList';
import MovieDetails from './components/MovieDetails';
import CollectionsPage from './components/collectionsPage';
import FavoritesPage from './components/FavoritesPage';
import FilterSidebar from './components/FilterSidebar';
import HeroSection from './components/HeroSection';
import LoadingSpinner from './components/LoadingSpinner';
import WatchlistPage from './components/WatchlistPage';

import { 
  getPopularMovies, 
  searchMovies, 
  getTrendingMovies,
  getTopRatedMovies,
  getUpcomingMovies,
  getNowPlayingMovies,
  discoverMoviesByGenre,
  getMoviesByRating,
  getMovieGenres
} from './lib/tmdb';
import { getTheme, setTheme, getStatistics } from './lib/storage';
import './Styles/tailwind.css';

// Theme Context
const ThemeContext = createContext();

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(true);
  
  useEffect(() => {
    const savedTheme = getTheme();
    setIsDark(savedTheme === 'dark');
  }, []);
  
  const toggleTheme = () => {
    const newTheme = isDark ? 'light' : 'dark';
    setTheme(newTheme);
    setIsDark(!isDark);
  };
  
  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <div className={isDark ? 'dark' : ''}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Main App Component
const App = () => {
  const [movies, setMovies] = useState([]);
  const [npages, setNPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('popular');
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genres, setGenres] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [featuredMovies, setFeaturedMovies] = useState([]);
  const [trendingMovies, setTrendingMovies] = useState([]);
  const [topRatedMovies, setTopRatedMovies] = useState([]);
  const [upcomingMovies, setUpcomingMovies] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [sortBy, setSortBy] = useState('popularity.desc');
  const [minRating, setMinRating] = useState(0);
  const [yearRange, setYearRange] = useState({ min: 1900, max: new Date().getFullYear() });

  // Load initial data
  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load multiple sections concurrently
      const [
        popularResult,
        genresResult,
        trendingResult,
        topRatedResult,
        upcomingResult
      ] = await Promise.allSettled([
        getPopularMovies(1),
        getMovieGenres(),
        getTrendingMovies('week', 1),
        getTopRatedMovies(1),
        getUpcomingMovies(1)
      ]);

      // Set main movies
      if (popularResult.status === 'fulfilled') {
        setMovies(popularResult.value.movies);
        setNPages(popularResult.value.totalPages);
        setFeaturedMovies(popularResult.value.movies.slice(0, 5));
      }

      // Set genres
      if (genresResult.status === 'fulfilled') {
        setGenres(genresResult.value);
      }

      // Set additional sections
      if (trendingResult.status === 'fulfilled') {
        setTrendingMovies(trendingResult.value.movies.slice(0, 12));
      }

      if (topRatedResult.status === 'fulfilled') {
        setTopRatedMovies(topRatedResult.value.movies.slice(0, 12));
      }

      if (upcomingResult.status === 'fulfilled') {
        setUpcomingMovies(upcomingResult.value.movies.slice(0, 12));
      }

      // Load user stats
      updateUserStats();

    } catch (err) {
      setError('Failed to load movies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update user statistics
  const updateUserStats = () => {
    const stats = getStatistics();
    setUserStats(stats);
  };

  // Load movies by filter
  const loadMoviesByFilter = async (filter, page = 1, genre = null) => {
    try {
      setLoading(true);
      setError(null);

      let result;
      switch (filter) {
        case 'trending':
          result = await getTrendingMovies('week', page);
          break;
        case 'top_rated':
          result = await getTopRatedMovies(page);
          break;
        case 'upcoming':
          result = await getUpcomingMovies(page);
          break;
        case 'now_playing':
          result = await getNowPlayingMovies(page);
          break;
        case 'genre':
          if (genre) {
            result = await discoverMoviesByGenre(genre.id, page, sortBy);
          }
          break;
        case 'high_rated':
          result = await getMoviesByRating(8, 10, page);
          break;
        default:
          result = await getPopularMovies(page);
      }

      if (result) {
        setMovies(result.movies);
        setNPages(result.totalPages);
        setCurrentPage(page);
      }

    } catch (err) {
      setError('Failed to load movies. Please try again.');
      setMovies([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    setSelectedGenre(null);
    
    if (query.trim()) {
      setActiveFilter('search');
      
      try {
        setLoading(true);
        const result = await searchMovies(query, 1);
        setMovies(result.movies);
        setNPages(result.totalPages);
        setCurrentPage(1);
      } catch (err) {
        setError('Search failed. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setActiveFilter('popular');
      await loadMoviesByFilter('popular', 1);
    }
  };

  // Handle filter change
  const handleFilterChange = async (filter, genre = null) => {
    setActiveFilter(filter);
    setSelectedGenre(genre);
    setCurrentPage(1);
    setSearchQuery('');
    
    await loadMoviesByFilter(filter, 1, genre);
  };

  // Handle page change
  const handlePageChange = async (page) => {
    setCurrentPage(page);
    
    if (searchQuery) {
      try {
        setLoading(true);
        const result = await searchMovies(searchQuery, page);
        setMovies(result.movies);
      } catch (err) {
        setError('Failed to load page.');
      } finally {
        setLoading(false);
      }
    } else {
      await loadMoviesByFilter(activeFilter, page, selectedGenre);
    }
    
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get filter display info
  const getFilterInfo = () => {
    if (searchQuery) {
      return {
        title: `Search Results for "${searchQuery}"`,
        description: `Found ${movies.length} movies`
      };
    }
    
    if (selectedGenre) {
      return {
        title: `${selectedGenre.name} Movies`,
        description: `Discover the best ${selectedGenre.name.toLowerCase()} movies`
      };
    }
    
    switch (activeFilter) {
      case 'trending':
        return {
          title: 'Trending Movies',
          description: 'Most popular movies this week'
        };
      case 'top_rated':
        return {
          title: 'Top Rated Movies',
          description: 'Highest rated movies of all time'
        };
      case 'upcoming':
        return {
          title: 'Upcoming Movies',
          description: 'Movies coming soon to theaters'
        };
      case 'now_playing':
        return {
          title: 'Now Playing',
          description: 'Movies currently in theaters'
        };
      case 'high_rated':
        return {
          title: 'Premium Movies',
          description: 'Movies rated 8+ stars'
        };
      default:
        return {
          title: 'Popular Movies',
          description: 'Most popular movies right now'
        };
    }
  };

  const filterInfo = getFilterInfo();
  const showHero = activeFilter === 'popular' && !searchQuery && featuredMovies.length > 0;

  return (
    <ThemeProvider>
      <Router>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
          
          {/* Navigation */}
          <NavBar 
            search={handleSearch} 
            userStats={userStats}
            onToggleSidebar={() => setShowSidebar(!showSidebar)}
            showSidebar={showSidebar}
          />

          {/* Filter Sidebar */}
          <FilterSidebar
            genres={genres}
            activeFilter={activeFilter}
            selectedGenre={selectedGenre}
            sortBy={sortBy}
            minRating={minRating}
            yearRange={yearRange}
            onFilterChange={handleFilterChange}
            onSortChange={setSortBy}
            onRatingChange={setMinRating}
            onYearRangeChange={setYearRange}
            isOpen={showSidebar}
            onClose={() => setShowSidebar(false)}
          />

          {/* Main Content */}
          <div className={`transition-all duration-300 ${showSidebar ? 'lg:ml-80' : ''}`}>
            
            <Routes>
              {/* Home Route */}
              <Route 
                path="/Movies" 
                element={
                  <>
                    {/* Hero Section */}
                    {showHero && !loading && (
                      <HeroSection 
                        featuredMovies={featuredMovies}
                        onMovieSelect={(movie) => window.location.href = `/movie/${movie.id}`}
                      />
                    )}

                    {/* User Stats Bar */}
                    {userStats.totalFavorites > 0 && (
                      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                          <div className="flex items-center justify-center space-x-8 text-sm">
                            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                              <span>❤️ {userStats.totalFavorites} Favorites</span>
                            </div>
                            <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
                              <span>📚 {userStats.totalCollections} Collections</span>
                            </div>
                            <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                              <span>👁️ {userStats.totalWatched} Watched</span>
                            </div>
                            {userStats.averageUserRating > 0 && (
                              <div className="flex items-center space-x-2 text-yellow-600 dark:text-yellow-400">
                                <span>⭐ Avg: {userStats.averageUserRating}★</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Loading State */}
                    {loading && <LoadingSpinner />}

                    {/* Error State */}
                    {error && !loading && (
                      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                              <span className="text-red-600 dark:text-red-400">⚠️</span>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-red-900 dark:text-red-200">
                                Oops! Something went wrong
                              </h3>
                              <p className="text-red-700 dark:text-red-300">{error}</p>
                              <button
                                onClick={initializeApp}
                                className="mt-2 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                              >
                                Try Again
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Movies Section */}
                    {!loading && !error && (
                      <>
                        {/* Section Header */}
                        {!showHero && (
                          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                            <div className="mb-8">
                              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                {filterInfo.title}
                              </h1>
                              <p className="text-gray-600 dark:text-gray-400">
                                {filterInfo.description}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Movies List */}
                        <MoviesList
                          movies={movies}
                          getPageMovies={handlePageChange}
                          npages={npages}
                          currentPage={currentPage}
                          onMovieUpdate={updateUserStats}
                          showHero={showHero}
                        />

                        {/* Additional Sections for Home Page */}
                        {showHero && (
                          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
                            
                            {/* Trending Section */}
                            {trendingMovies.length > 0 && (
                              <section>
                                <div className="flex items-center justify-between mb-6">
                                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    🔥 Trending This Week
                                  </h2>
                                  <button
                                    onClick={() => handleFilterChange('trending')}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                                  >
                                    See All →
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                  {trendingMovies.map((movie) => (
                                    <div key={movie.id} className="transform hover:scale-105 transition-transform duration-200">
                                      {/* Movie card will be rendered by MoviesList component */}
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}

                            {/* Top Rated Section */}
                            {topRatedMovies.length > 0 && (
                              <section>
                                <div className="flex items-center justify-between mb-6">
                                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    ⭐ Top Rated Movies
                                  </h2>
                                  <button
                                    onClick={() => handleFilterChange('top_rated')}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                                  >
                                    See All →
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                  {topRatedMovies.slice(0, 6).map((movie) => (
                                    <div key={movie.id} className="transform hover:scale-105 transition-transform duration-200">
                                      {/* Movie card will be rendered here */}
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}

                            {/* Coming Soon Section */}
                            {upcomingMovies.length > 0 && (
                              <section>
                                <div className="flex items-center justify-between mb-6">
                                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    🎬 Coming Soon
                                  </h2>
                                  <button
                                    onClick={() => handleFilterChange('upcoming')}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                                  >
                                    See All →
                                  </button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                  {upcomingMovies.slice(0, 6).map((movie) => (
                                    <div key={movie.id} className="transform hover:scale-105 transition-transform duration-200">
                                      {/* Movie card will be rendered here */}
                                    </div>
                                  ))}
                                </div>
                              </section>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                }
              />

              {/* Movie Details Route */}
              <Route 
                path="/movie/:id" 
                element={
                  <MovieDetails 
                    onMovieUpdate={updateUserStats}
                  />
                } 
              />

              {/* Collections Route */}
              <Route 
                path="/collections" 
                element={
                  <CollectionsPage 
                    onUpdate={updateUserStats}
                  />
                } 
              />

              {/* Favorites Route */}
              <Route 
                path="/favorites" 
                element={
                  <FavoritesPage 
                    onUpdate={updateUserStats}
                  />
                } 
              />

              {/* Watchlist Route */}
              <Route 
                path="/watchlist" 
                element={
                  <WatchlistPage 
                    onUpdate={updateUserStats}
                  />
                } 
              />
            </Routes>
          </div>
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;